import React, { createContext, useContext, useEffect, useState, useCallback, useRef, startTransition } from 'react'
import { supabase } from '@/lib/supabase'
import type { Profile, Obra, Company, CompanyLimits } from '@/lib/types'
import { readImpersonationCompanyId } from '@/contexts/AdminImpersonationContext'
import { logAuthError } from '@/lib/systemLogger'

/**
 * Resolve o company_id efetivo: se o Super Admin está impersonando uma empresa,
 * usa o ID impersonado; caso contrário, usa o do próprio perfil.
 */
function resolveEffectiveCompanyId(profileCompanyId?: string | null): string | null {
  const impersonated = readImpersonationCompanyId()
  if (impersonated) return impersonated
  return profileCompanyId || null
}

interface AuthContextType {
  user: Profile | null
  loading: boolean
  blocked: boolean
  obras: Obra[]
  obrasLoaded: boolean
  obraAtiva: Obra | null
  company: Company | null
  companyLimits: CompanyLimits | null
  setObraAtiva: (obra: Obra) => void
  loadObras: () => Promise<void>
  loadCompany: () => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const [blocked, setBlocked] = useState(false)
  const [obras, setObras] = useState<Obra[]>([])
  const [obrasLoaded, setObrasLoaded] = useState(false)
  const [obraAtiva, setObraAtivaState] = useState<Obra | null>(null)
  const [company, setCompany] = useState<Company | null>(null)
  const [companyLimits, setCompanyLimits] = useState<CompanyLimits | null>(null)
  const limitsLastLoadedRef = useRef<{ cid: string; at: number } | null>(null)

  const loadProfile = useCallback(async (authUser: { id: string; email?: string }): Promise<Profile | null> => {
    let data: any = null
    const { data: d1, error } = await supabase
      .from('profiles')
      .select('id,nome,cargo,perfil,status,modulos_ativos,company_id,company_role')
      .eq('id', authUser.id)
      .maybeSingle()

    if (error && error.code === '42703') {
      // Fallback: colunas novas ainda não existem no schema
      const { data: d2, error: e2 } = await supabase
        .from('profiles')
        .select('id,nome,cargo,perfil,status')
        .eq('id', authUser.id)
        .maybeSingle()
      if (e2) {
        console.error('[AuthContext] Erro ao carregar perfil (fallback):', e2)
        void logAuthError('Falha ao carregar perfil (fallback schema)', {
          action: 'load-profile-fallback',
          errorCode: e2?.code,
          userId: authUser.id,
        })
        return null
      }
      data = d2 ? { ...d2, modulos_ativos: [], company_id: null, company_role: 'viewer' } : null
    } else if (error) {
      console.error('[AuthContext] Erro ao carregar perfil:', error)
      void logAuthError('Falha ao carregar perfil', {
        action: 'load-profile',
        errorCode: error?.code,
        userId: authUser.id,
      })
      return null
    } else {
      data = d1
    }

    if (!data) {
      console.error('[AuthContext] Perfil não encontrado para', authUser.id)
      // NÃO faz signOut aqui — pode ser falha de rede temporária no mobile.
      // Retorna null para que o boot marque blocked=true e mostre tela de erro,
      // preservando o token de sessão para que o usuário possa tentar de novo
      // sem precisar logar novamente.
      void logAuthError('Perfil não encontrado — sessão preservada', {
        action: 'profile-not-found',
        userId: authUser.id,
      })
      return null
    }

    // Status check: bloqueia mas NÃO expulsa a sessão.
    // Fazer signOut aqui causa loop: usuário loga → expulso → loga → expulso.
    // Conta com status diferente de 'aprovado' deve ver tela de "aguardando aprovação".
    if (data.status && data.status !== 'aprovado') {
      void logAuthError('Perfil com status bloqueante — sessão preservada', {
        action: 'profile-blocked',
        userId: authUser.id,
      })
      return null
    }

    // A verificação de empresa ativa (is_active) foi movida para o ProtectedRoute.
    // O AuthContext apenas gerencia a sessão do usuário, sem bloquear ou
    // fazer logout baseado no status da empresa.

    const profile: Profile = { ...data, email: authUser.email, modulos_ativos: data.modulos_ativos || [] }
    startTransition(() => setUser(profile))
    return profile
  }, [])

  const loadCompanyInternal = useCallback(async (companyId?: string) => {
    const cid = companyId || resolveEffectiveCompanyId(user?.company_id)
    if (!cid) {
      startTransition(() => { setCompany(null); setCompanyLimits(null) })
      return
    }

    try {
      const LIMITS_TTL = 60 * 60 * 1000 // 1h — planos raramente mudam durante a sessão
      const now = Date.now()
      const limitsStale = !limitsLastLoadedRef.current
        || limitsLastLoadedRef.current.cid !== cid
        || now - limitsLastLoadedRef.current.at > LIMITS_TTL

      const requests: [Promise<any>, Promise<any>] = [
        Promise.resolve(supabase.from('companies').select('*').eq('id', cid).single()),
        limitsStale
          ? Promise.resolve(supabase.rpc('check_company_limits', { p_company_id: cid }))
          : Promise.resolve({ data: companyLimits }),
      ]
      const [{ data: companyData }, { data: limits }] = await Promise.all(requests)
      if (limitsStale && limits) limitsLastLoadedRef.current = { cid, at: now }
      startTransition(() => {
        if (companyData) setCompany(companyData as Company)
        if (limits) setCompanyLimits(limits as CompanyLimits)
      })
    } catch (e) {
      console.error('[AuthContext] loadCompanyInternal error (não bloqueante):', e)
    }
  }, [user?.company_id])

  const loadCompany = useCallback(async () => {
    await loadCompanyInternal()
  }, [loadCompanyInternal])

  const loadObrasInternal = useCallback(async (companyId?: string): Promise<Obra[] | null> => {
    const cid = companyId || resolveEffectiveCompanyId(user?.company_id)
    const cacheKey = `construzilla_cache_obras_${cid || 'all'}`

    if (!cid) {
      // [log removido — dados sensíveis]
      return null
    }

    const query = supabase
      .from('vw_resumo_obras')
      .select('*')
      .eq('company_id', cid)
      .order('nome')

    const loadObrasFallback = async (): Promise<Obra[] | null> => {
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('obras')
        .select('id,nome,cliente_nome,endereco,tipologia,status,avanco_fisico,etapa_atual,data_inicio,data_termino_prev,company_id')
        .eq('company_id', cid)
        .order('nome', { ascending: true })

      if (fallbackError) return null
      const fallbackObras: Obra[] = ((fallbackData || []) as any[]).map((o): Obra => ({
        ...o,
        status: o.status || 'em_execucao',
        avanco_fisico: o.avanco_fisico ?? 0,
      }))
      startTransition(() => { setObras(fallbackObras); setObrasLoaded(true) })
      try {
        localStorage.setItem(cacheKey, JSON.stringify({ data: fallbackObras, timestamp: Date.now() }))
      } catch { /* storage indisponível — ignorado */ }
      return fallbackObras
    }

    try {
      const { data, error } = await query
      if (error) throw error

      // P1-05: Also load obras where user is external contractor (different company)
      // Only for self-load (not impersonation) and only if user has a profile
      let allObras: Obra[] = (data || []) as Obra[]
      if (!companyId && user?.id && user?.company_id) {
        const { data: extMembros } = await supabase
          .from('obra_membros')
          .select('obra_id, obras(id, nome, status, company_id, avanco_fisico, etapa_atual, tipologia, data_inicio, data_termino_prev, endereco)')
          .eq('profile_id', user.id)
          .eq('external_user', true)
          .eq('ativo', true)

        const extObras: Obra[] = (extMembros || [])
          .map((m: any) => m.obras)
          .filter(Boolean)
          .filter((o: any) => o.company_id !== user.company_id) // exclude own company
          .map((o: any): Obra => ({ ...o, avanco_fisico: o.avanco_fisico ?? 0 }))

        if (extObras.length > 0) {
          const ownIds = new Set(allObras.map(o => o.id))
          allObras = [...allObras, ...extObras.filter(o => !ownIds.has(o.id))]
        }
      }

      if (allObras.length > 0 || data) {
        startTransition(() => { setObras(allObras); setObrasLoaded(true) })
        try {
          localStorage.setItem(cacheKey, JSON.stringify({ data: allObras, timestamp: Date.now() }))
        } catch { /* storage indisponível — ignorado */ }
      } else {
        setObrasLoaded(true)
      }
      return allObras
    } catch (e) {
      // Se a view agregada der timeout, usa a tabela base para não bloquear seleção de obra.
      const fallbackObras = await loadObrasFallback()
      if (fallbackObras) return fallbackObras

      // Offline / falha de rede — tenta cache
      try {
        const cached = localStorage.getItem(cacheKey)
        if (cached) {
          const parsed = JSON.parse(cached) as { data: Obra[]; timestamp: number }
          if (parsed.data) {
            startTransition(() => setObras(parsed.data))
            console.warn('[AuthContext] Carregando obras do cache offline')
            return parsed.data
          }
        }

      } catch { /* storage indisponível — ignorado */ }
      return null
    }
  }, [user?.company_id])

  const loadObras = useCallback(async () => {
    await loadObrasInternal()
  }, [loadObrasInternal])

  const setObraAtiva = useCallback((obra: Obra) => {
    setObraAtivaState(obra)
    localStorage.setItem('cpro_ativa', obra.id)
    try { localStorage.setItem('cz:obra_id', obra.id) } catch { /* noop */ }
  }, [])

  // Sincroniza company_id ativo no localStorage para systemLogger
  useEffect(() => {
    try {
      const effective = resolveEffectiveCompanyId(user?.company_id)
      if (effective) localStorage.setItem('cz:company_id', effective)
      else localStorage.removeItem('cz:company_id')
    } catch { /* noop */ }
  }, [user?.company_id])

  const logout = useCallback(async () => {
    await supabase.auth.signOut()
    startTransition(() => {
      setUser(null)
      setObras([])
      setObraAtivaState(null)
      setCompany(null)
      setCompanyLimits(null)
    })
    try {
      localStorage.removeItem('cz:company_id')
      localStorage.removeItem('cz:obra_id')
    } catch { /* noop */ }
  }, [])

  // Refs para evitar dependências instáveis no useEffect de boot
  const loadProfileRef = useRef(loadProfile)
  const loadCompanyInternalRef = useRef(loadCompanyInternal)
  const loadObrasInternalRef = useRef(loadObrasInternal)
  const setObraAtivaRef = useRef(setObraAtiva)
  // Guard para evitar recarregamento duplicado quando SIGNED_IN dispara
  // múltiplas vezes para a mesma sessão (HMR, INITIAL_SESSION, etc.)
  const lastLoadedUserIdRef = useRef<string | null>(null)
  useEffect(() => { loadProfileRef.current = loadProfile }, [loadProfile])
  useEffect(() => { loadCompanyInternalRef.current = loadCompanyInternal }, [loadCompanyInternal])
  useEffect(() => { loadObrasInternalRef.current = loadObrasInternal }, [loadObrasInternal])
  useEffect(() => { setObraAtivaRef.current = setObraAtiva }, [setObraAtiva])

  useEffect(() => {
    let cancelled = false
    let mounted = true

    // Safety timeout: garante que loading nunca fica preso se getSession() travar
    // (localStorage corrompido, PKCE pendente, rede offline no boot, etc.)
    const safetyTimer = setTimeout(() => {
      if (!cancelled && mounted) {
        console.warn('[AuthContext] safety timeout — forçando setLoading(false)')
        startTransition(() => setLoading(false))
      }
    }, 10000)

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (cancelled || !mounted) return
      if (session?.user) {
        lastLoadedUserIdRef.current = session.user.id
        const profile = await loadProfileRef.current(session.user)
        if (cancelled || !mounted) return
        if (!profile) {
          startTransition(() => { setBlocked(true); setLoading(false) })
          return
        }
        const effectiveCompanyId = resolveEffectiveCompanyId(profile.company_id)
        if (effectiveCompanyId) {
          await loadCompanyInternalRef.current(effectiveCompanyId)
        }
        if (cancelled || !mounted) return
        const obrasList = await loadObrasInternalRef.current(effectiveCompanyId || undefined)
        if (cancelled || !mounted) return
        const saved = localStorage.getItem('cpro_ativa')
        if (obrasList?.length) {
          const toAct = (saved && obrasList.find((o: Obra) => o.id === saved)) || obrasList[0]
          if (toAct) setObraAtivaRef.current(toAct)
        }
      }
      if (!cancelled && mounted) startTransition(() => setLoading(false))
    }).catch((e: unknown) => {
      console.error(`[AuthContext] boot error`, e)
      if (!cancelled && mounted) startTransition(() => setLoading(false))
    }).finally(() => {
      clearTimeout(safetyTimer)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return

      if (event === 'SIGNED_OUT') {
        lastLoadedUserIdRef.current = null
        // Usa startTransition para não corromper o DOM durante ciclo de commit
        startTransition(() => {
          setUser(null)
          setObras([])
          setObraAtivaState(null)
          setCompany(null)
          setCompanyLimits(null)
        })
        return
      }

      if (event === 'TOKEN_REFRESHED' && session?.user) {
        // Token renovado — não recarrega dados; o token novo é usado
        // automaticamente nas próximas requisições. Recarregar aqui causa
        // chamadas desnecessárias e loops com onAuthStateChange.
        return
      }

      if (event === 'SIGNED_IN' && session?.user && !cancelled) {
        // Pula se já carregamos esta sessão (INITIAL_SESSION/HMR podem
        // disparar SIGNED_IN para o mesmo usuário já carregado pelo boot).
        if (lastLoadedUserIdRef.current === session.user.id) return
        lastLoadedUserIdRef.current = session.user.id
        // Login em outra aba, SSO ou re-login após ajuste de perfil —
        // recarrega perfil, company e obras de forma assíncrona.
        setTimeout(async () => {
          if (!mounted) return
          const { data: { session: currentSession } } = await supabase.auth.getSession()
          if (!currentSession?.access_token || !mounted) return
          const profile = await loadProfileRef.current(session.user)
          if (!profile || !mounted) return
          const effectiveCompanyId = resolveEffectiveCompanyId(profile.company_id)
          if (effectiveCompanyId) await loadCompanyInternalRef.current(effectiveCompanyId)
          if (!mounted) return
          await loadObrasInternalRef.current(effectiveCompanyId || undefined)
        }, 0)
      }
    })

    return () => {
      cancelled = true
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, blocked, obras, obrasLoaded, obraAtiva, company, companyLimits, setObraAtiva, loadObras, loadCompany, logout }}>
      {children}
    </AuthContext.Provider>
  )
}
